import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react'
import { useAuthIdentity, useServiceAuth } from '@/contexts/AuthContext'
import type { ChartTemplate, ActiveTemplateState } from '@/services/templates/schemas/templateSchema'
import { TemplateService } from '@/services/templates/templateService'
import type { ChartConfig } from '@/interfaces/waveChart'

// =============================================================================
// Types
// =============================================================================

interface TemplateState {
  templates: ChartTemplate[]
  activeTemplate: ActiveTemplateState
  isLoading: boolean
  error: string | null
}

// =============================================================================
// Actions
// =============================================================================

type TemplateAction =
  | { type: 'SET_TEMPLATES'; payload: ChartTemplate[] }
  | { type: 'ADD_TEMPLATE'; payload: ChartTemplate }
  | { type: 'UPDATE_TEMPLATE'; payload: { id: string; template: ChartTemplate } }
  | { type: 'REMOVE_TEMPLATE'; payload: string }
  | { type: 'SET_ACTIVE_TEMPLATE'; payload: { id: string | undefined; hasUnsavedChanges: boolean } }
  | { type: 'MARK_TEMPLATE_MODIFIED' }
  | { type: 'CLEAR_UNSAVED_CHANGES' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }

// =============================================================================
// Reducer
// =============================================================================

const initialState: TemplateState = {
  templates: [],
  activeTemplate: {
    activeTemplateId: undefined,
    hasUnsavedChanges: false
  },
  isLoading: false,
  error: null
}

function templateReducer(state: TemplateState, action: TemplateAction): TemplateState {
  switch (action.type) {
    case 'SET_TEMPLATES':
      console.log('🔧 TEMPLATE_REDUCER: SET_TEMPLATES called with', action.payload.length, 'templates:', action.payload.map(t => t.id))
      return { ...state, templates: action.payload }
    
    case 'ADD_TEMPLATE':
      return { ...state, templates: [...state.templates, action.payload] }
    
    case 'UPDATE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.map(template =>
          template.id === action.payload.id ? action.payload.template : template
        )
      }
    
    case 'REMOVE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.filter(template => template.id !== action.payload),
        // Clear active template if it was deleted
        activeTemplate: state.activeTemplate.activeTemplateId === action.payload
          ? { activeTemplateId: undefined, hasUnsavedChanges: false }
          : state.activeTemplate
      }
    
    case 'SET_ACTIVE_TEMPLATE':
      return {
        ...state,
        activeTemplate: {
          activeTemplateId: action.payload.id,
          hasUnsavedChanges: action.payload.hasUnsavedChanges
        }
      }
    
    case 'MARK_TEMPLATE_MODIFIED':
      return {
        ...state,
        activeTemplate: {
          ...state.activeTemplate,
          hasUnsavedChanges: true
        }
      }
    
    case 'CLEAR_UNSAVED_CHANGES':
      return {
        ...state,
        activeTemplate: {
          ...state.activeTemplate,
          hasUnsavedChanges: false
        }
      }
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    
    default:
      return state
  }
}

// =============================================================================
// Context
// =============================================================================

interface TemplateContextValue {
  // State
  templates: ChartTemplate[]
  activeTemplate: ActiveTemplateState
  isLoading: boolean
  error: string | null
  
  // Actions
  loadTemplates: () => Promise<void>
  createTemplate: (name: string, chartConfigs: ChartConfig[]) => Promise<void>
  updateTemplate: (templateId: string, chartConfigs: ChartConfig[]) => Promise<void>
  deleteTemplate: (templateId: string) => Promise<void>
  
  // Template application
  setActiveTemplate: (templateId: string | undefined) => void
  markTemplateModified: () => void
  clearUnsavedChanges: () => void
  
  // Computed
  isErdosMiller: boolean
  currentUserId: string | null
}

const TemplateContext = createContext<TemplateContextValue | null>(null)

// =============================================================================
// Hook
// =============================================================================

export function useTemplates() {
  const context = useContext(TemplateContext)
  if (!context) {
    throw new Error('useTemplates must be used within a TemplateProvider')
  }
  return context
}

// =============================================================================
// Provider
// =============================================================================

interface TemplateProviderProps {
  children: React.ReactNode
}

export function TemplateProvider({ children }: TemplateProviderProps) {
  const [state, dispatch] = useReducer(templateReducer, initialState)
  const { userId, organizationId, isErdosMiller } = useAuthIdentity()
  const getServiceAuth = useServiceAuth()

  // User info from clean auth context - get these fresh but don't watch them
  const currentUserId = userId

  // Load templates from database
  const loadTemplates = useCallback(async () => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) return
    
    const templateService = new TemplateService(serviceAuth)

    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })

    try {
      console.log('🔄 TEMPLATE_CONTEXT: Loading templates from database...')
      const response = await templateService.searchTemplates({
        // Load all accessible templates (private + org + public)
      })
      
      console.log('✅ TEMPLATE_CONTEXT: Templates loaded:', response.templates.length, 'IDs:', response.templates.map(t => t.id))
      dispatch({ type: 'SET_TEMPLATES', payload: response.templates })
    } catch (error) {
      console.error('❌ Failed to load templates:', error)
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to load templates' 
      })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [getServiceAuth])

  // Load templates on mount - only when auth identity changes
  useEffect(() => {
    if (userId && organizationId) {
      loadTemplates()
    }
  }, [userId, organizationId, loadTemplates])

  // Create new template
  const createTemplate = useCallback(async (
    name: string, 
    chartConfigs: ChartConfig[]
  ) => {
    console.log('TEMPLATE_CONTEXT: createTemplate called:', { name, chartConfigsCount: chartConfigs.length })
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    console.log('TEMPLATE_CONTEXT: Auth state:', { userId: serviceAuth?.userId, organizationId: serviceAuth?.orgId })
    
    if (!serviceAuth) {
      console.error('TEMPLATE_CONTEXT: Authentication not available')
      throw new Error('Template service not available - please ensure you are signed in')
    }
    
    const templateService = new TemplateService(serviceAuth)

    try {
      console.log('🔄 Creating new template:', { name, chartConfigsCount: chartConfigs.length })
      
      const newTemplate = await templateService.createTemplate({
        name,
        description: `Template created from current workspace configuration`,
        chartConfigs,
        accessLevel: 'private' // Default to private access
      })
      
      console.log('✅ Template created successfully:', newTemplate)
      
      dispatch({ type: 'ADD_TEMPLATE', payload: newTemplate })
      dispatch({ 
        type: 'SET_ACTIVE_TEMPLATE', 
        payload: { id: newTemplate.id, hasUnsavedChanges: false } 
      })
      
    } catch (error) {
      console.error('❌ Failed to create template:', error)
      throw error
    }
  }, [getServiceAuth])

  // Update existing template
  const updateTemplate = useCallback(async (
    templateId: string, 
    chartConfigs: ChartConfig[]
  ) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      throw new Error('Template service not available')
    }
    
    const templateService = new TemplateService(serviceAuth)

    const template = state.templates.find(t => t.id === templateId)
    if (!template) {
      throw new Error('Template not found')
    }

    try {
      console.log('🔄 Updating template:', { templateId, chartConfigsCount: chartConfigs.length })
      
      await templateService.updateTemplate(templateId, { chartConfigs })
      
      // Update local state
      const updatedTemplate = { ...template, chartConfigs }
      dispatch({ 
        type: 'UPDATE_TEMPLATE', 
        payload: { id: templateId, template: updatedTemplate } 
      })
      dispatch({ type: 'CLEAR_UNSAVED_CHANGES' })
      
      console.log('✅ Template updated successfully')
    } catch (error) {
      console.error('❌ Failed to update template:', error)
      throw error
    }
  }, [getServiceAuth, state.templates])

    // Delete template
  const deleteTemplate = useCallback(async (templateId: string) => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      throw new Error('Template service not available')
    }

    const templateService = new TemplateService(serviceAuth)

    try {
      console.log('🔄 Deleting template:', templateId)
      
      await templateService.deleteTemplate(templateId)
      dispatch({ type: 'REMOVE_TEMPLATE', payload: templateId })
      
      console.log('✅ Template deleted successfully')
    } catch (error) {
      console.error('❌ Failed to delete template:', error)
      throw error
    }
  }, [getServiceAuth])

  // Set active template
  const setActiveTemplate = useCallback((templateId: string | undefined) => {
    dispatch({ 
      type: 'SET_ACTIVE_TEMPLATE', 
      payload: { id: templateId, hasUnsavedChanges: false } 
    })
  }, [])

  // Mark template as modified
  const markTemplateModified = useCallback(() => {
    dispatch({ type: 'MARK_TEMPLATE_MODIFIED' })
  }, [])

  // Clear unsaved changes
  const clearUnsavedChanges = useCallback(() => {
    dispatch({ type: 'CLEAR_UNSAVED_CHANGES' })
  }, [])

  const contextValue: TemplateContextValue = {
    templates: state.templates,
    activeTemplate: state.activeTemplate,
    isLoading: state.isLoading,
    error: state.error,
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setActiveTemplate,
    markTemplateModified,
    clearUnsavedChanges,
    isErdosMiller,
    currentUserId
  }

  return (
    <TemplateContext.Provider value={contextValue}>
      {children}
    </TemplateContext.Provider>
  )
}